/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { getAncestorsAndSelf } from 'src/core/utils/tree';
import { useModels } from 'src/luis/modules/models/useModels';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { HierarchicalEntity } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { ListEntity } from 'src/luis/projects/entities/types/ListEntity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { entityDenormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityRoleStore } from 'src/luis/types/stores/entities/EntityRoleStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { CompositeEntityAdvancedOptions } from 'src/luis/verticals/labelingSidePane/components/CompositeEntityAdvancedOptions';
import { HierarchicalEntityAdvancedOptions } from 'src/luis/verticals/labelingSidePane/components/HierarchicalEntityAdvancedOptions';
import { ListEntityAdvancedOptions } from 'src/luis/verticals/labelingSidePane/components/ListEntityAdvacedOptions';
import { MLEntityAdvancedOptions } from 'src/luis/verticals/labelingSidePane/components/MLEntityAdvancedOptions';
import { NoAdvancedOptions } from 'src/luis/verticals/labelingSidePane/components/NoAdvancedOptions';
import { renderAdvancedOptionsTitle } from 'src/luis/verticals/labelingSidePane/components/renderAdvancedOptionTitle';
import { RoleBasedEntityAdvancedOptions } from 'src/luis/verticals/labelingSidePane/components/RoleBasedEntityAdvancedOptions';

const { styleComponent } = getStylistV2('AdvancedOptionsPane');

const Root = styleComponent<IStackProps>(Stack)('Root', theme => ({
    background: theme.backgroundColor,
    padding: '0 12px 12px 12px',
    overflowY: 'auto'
}));

const EmptyText = styleComponent<LuisTextProps>(LuisText)('EmptyText', theme => ({
    color: theme.emptyTextColor,
    fontSize: 12,
    marginTop: 12
}));

const messages = defineMessages({
    emptyMessage: {
        id: 'luis.intentLabelingSidePane.advancedOptionsPane.emptyMessage',
        defaultMessage: 'To add advanced options, select an entity or subentity above.'
    }
});

type AdvancedOptionsPaneProps = {
    /**
     * Entity store to render advanced options for.
     */
    entityStore: EntityStore;
    /**
     * Root entity store for simple entity stores.
     */
    rootEntityStore: EntityStore;
    /**
     * Selected role store for role based entities.
     */
    selectedRole: EntityRoleStore;
    /**
     * Selected normalized value for list entities.
     */
    selectedNormalizedValue: string;
    /**
     * Selected hierarchical child id.
     */
    selectedHierarchicalChildId: LuisId;
    /**
     * Optional: Indicates if an async task is progressing.
     */
    isProgressing?: boolean;
    /**
     * Callback for creating a new role.
     */
    onCreateRole: (entity: Entity, role: string) => void;
    /**
     * Callback for selecting a new role.
     */
    onSelectRole: (entity: Entity, roleId: LuisId) => void;
    /**
     * Callback for creating a new normalized value.
     */
    onCreateNormalizedValue: (entity: Entity, normalizedValue: string) => void;
    /**
     * Callback for creating a new normalized value.
     */
    onSelectNormalizedValue: (entity: Entity, normalizedValue: string) => void;
    /**
     * Callback for selecting a hierarchical entity child.
     */
    onSelectHierarchicalChildEntity: (entity: Entity, childEntityId: LuisId) => void;
};

const renderOptions = (
    entity: Entity,
    rootEntityId: LuisId,
    options?: Omit<AdvancedOptionsPaneProps, 'entityStore' | 'rootEntityStore'>
): React.ReactNode => {
    const { selectedHierarchicalChildId, onSelectHierarchicalChildEntity, ...restProps } = options;

    switch (entity.typeId) {
        case ModelType.Simple:
            return <MLEntityAdvancedOptions entity={entity as SimpleEntity} rootEntityId={rootEntityId} {...restProps} />;
        case ModelType.Regex:
        case ModelType.Prebuilt:
            return <RoleBasedEntityAdvancedOptions entity={entity} {...restProps} />;
        case ModelType.List:
            return <ListEntityAdvancedOptions entity={entity as ListEntity} {...restProps} />;
        case ModelType.Hierarchical:
            return (
                <HierarchicalEntityAdvancedOptions
                    entity={entity as HierarchicalEntity}
                    onSelectChildEntity={onSelectHierarchicalChildEntity}
                    selectedEntityId={selectedHierarchicalChildId}
                    {...restProps}
                />
            );
        case ModelType.Composite:
            return <CompositeEntityAdvancedOptions entity={entity as CompositeEntity} {...options} />;
        default:
            return <NoAdvancedOptions />;
    }
};

const getPaneTitle = (entity: Entity, rootEntity: Entity) => {
    switch (entity.typeId) {
        case ModelType.Simple:
            const entities = rootEntity ? getAncestorsAndSelf<SimpleEntity>(rootEntity as SimpleEntity, e => e.id === entity.id) : [entity];

            return entities.map(e => e.name).join(' / ');
        default:
            return entity.name;
    }
};

export const AdvancedOptionsPane = React.memo(
    observer((props: AdvancedOptionsPaneProps) => {
        const { entityStore, rootEntityStore, ...options } = props;

        const { localize } = useLocalization();
        const { entityStores, intentStores, phraseListStores } = useModels();
        const denormalizer = entityDenormalizer(entityStores, intentStores, phraseListStores);

        const entity = entityStore ? denormalizer(entityStore) : null;
        const rootEntity = rootEntityStore ? denormalizer(rootEntityStore) : null;

        return (
            <Root grow>
                {entity ? (
                    <Stack tokens={{ childrenGap: 8 }}>
                        {renderAdvancedOptionsTitle(getPaneTitle(entity, rootEntity))}
                        {renderOptions(entity, rootEntity?.id, options)}
                    </Stack>
                ) : (
                    <EmptyText>{localize(messages.emptyMessage)}</EmptyText>
                )}
            </Root>
        );
    })
);
